import { useState, useEffect, useRef } from 'react'
import type { Message, UserData } from '../App'
import './ChatInterface.css'

interface ChatInterfaceProps {
  messages: Message[]
  onSendMessage: (message: string) => void
  onSendFile: (file: File, message?: string) => Promise<void>
  isLoading: boolean
  userData: UserData | null
}

interface FilePreview {
  file: File
  previewUrl: string | null
  type: 'image' | 'pdf'
}

export default function ChatInterface({
  messages,
  onSendMessage,
  onSendFile,
  isLoading,
  userData,
}: ChatInterfaceProps) {
  const [input, setInput] = useState('')
  const [filePreview, setFilePreview] = useState<FilePreview | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading) return

    // Si hay archivo adjunto, enviarlo
    if (filePreview) {
      await onSendFile(filePreview.file, input.trim() || undefined)
      clearFilePreview()
      setInput('')
      return
    }

    // Solo texto
    if (input.trim()) {
      onSendMessage(input.trim())
      setInput('')
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tipo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']
    if (!allowedTypes.includes(file.type)) {
      alert('Tipo de archivo no permitido. Solo imagenes (JPG, PNG, GIF, WEBP) y PDF.')
      return
    }

    // Validar tamano (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Maximo 10MB.')
      return
    }

    // Crear preview
    const isImage = file.type.startsWith('image/')
    let previewUrl: string | null = null

    if (isImage) {
      previewUrl = URL.createObjectURL(file)
    }

    setFilePreview({
      file,
      previewUrl,
      type: isImage ? 'image' : 'pdf'
    })

    // Limpiar input para permitir seleccionar el mismo archivo
    e.target.value = ''
  }

  const clearFilePreview = () => {
    if (filePreview?.previewUrl) {
      URL.revokeObjectURL(filePreview.previewUrl)
    }
    setFilePreview(null)
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  // Función para formatear texto con estilo WhatsApp
  const formatWhatsAppText = (text: string) => {
    // Escapar HTML para prevenir XSS
    let formatted = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')

    // Convertir URLs a links clickeables (después del escape para que los tags no se escapen)
    // La puntuación final pegada a la URL ("…com.ar.", "…,") queda FUERA del link.
    formatted = formatted.replace(/(https?:\/\/[^\s]+)/g, (match) => {
      const trailing = match.match(/[.,;:!?]+$/)?.[0] ?? ''
      const url = trailing ? match.slice(0, -trailing.length) : match
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="message-link">${url}</a>${trailing}`
    })

    // Aplicar solo saltos de línea (NO formateo de * _ ~ porque rompe URLs)
    formatted = formatted.replace(/\n/g, '<br />')

    return formatted
  }

  return (
    <div className="chat-interface">
      {/* Header */}
      <div className="chat-header">
        <div className="header-content">
          <div className="logo-container">
            <div className="logo-circle">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
          </div>
          <div className="header-info">
            <h1 className="header-title">
              {userData?.fullName || 'MejorHablemos'}
            </h1>
            <p className="header-subtitle">
              {userData ? (
                <>
                  {userData.email && <span>{userData.email}</span>}
                  {userData.email && (userData.city || userData.state) && <span> • </span>}
                  {userData.city && <span>{userData.city}</span>}
                  {userData.city && userData.state && <span>, </span>}
                  {userData.state && <span>{userData.state}</span>}
                  {!userData.email && !userData.city && !userData.state && <span>Asistente Virtual</span>}
                </>
              ) : (
                'Asistente Virtual'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="messages-container">
        <div className="messages">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.sender === 'user' ? 'message-user' : 'message-bot'}`}
            >
              <div className="message-bubble">
                <p
                  className="message-content"
                  dangerouslySetInnerHTML={{ __html: formatWhatsAppText(message.content) }}
                />
                <span className="message-time">{formatTime(message.timestamp)}</span>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message message-bot">
              <div className="message-bubble">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* File Preview */}
      {filePreview && (
        <div className="file-preview-container">
          <div className="file-preview">
            {filePreview.type === 'image' && filePreview.previewUrl ? (
              <img src={filePreview.previewUrl} alt="Preview" className="file-preview-image" />
            ) : (
              <div className="file-preview-pdf">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <span>{filePreview.file.name}</span>
              </div>
            )}
            <button
              type="button"
              className="file-preview-remove"
              onClick={clearFilePreview}
              aria-label="Quitar archivo"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="input-container">
        <form onSubmit={handleSubmit} className="input-form">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {/* Attach button */}
          <button
            type="button"
            className="attach-button"
            onClick={handleAttachClick}
            disabled={isLoading}
            aria-label="Adjuntar archivo"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>

          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={filePreview ? "Agrega un mensaje (opcional)..." : "Escribe tu mensaje..."}
            className="message-input"
            disabled={isLoading}
          />
          <button
            type="submit"
            className="send-button"
            disabled={(!input.trim() && !filePreview) || isLoading}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  )
}
